import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Gift, Copy, Users, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { useReferral } from '@/hooks/useReferral';

const REFERRAL_POPUP_DISMISSED_KEY = 'referral_popup_dismissed';

export function ReferralPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const { stats, loading } = useReferral();
  const { toast } = useToast();

  useEffect(() => {
    const isDismissed = localStorage.getItem(REFERRAL_POPUP_DISMISSED_KEY) === 'true';
    const justLoggedIn = sessionStorage.getItem('just_logged_in') === 'true';
    const welcomeShown = localStorage.getItem('welcome_popup_shown') === 'true';

    // Only show if:
    // 1. Not dismissed permanently
    // 2. Just logged in
    // 3. Welcome was already shown (so we don't overlap)
    if (!isDismissed && !loading && justLoggedIn && welcomeShown) {
      const timer = setTimeout(() => {
        setIsOpen(true);
        // Clean up sessionStorage after showing
        sessionStorage.removeItem('just_logged_in');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem(REFERRAL_POPUP_DISMISSED_KEY, 'true');
    }
    // Always clean up sessionStorage on close
    sessionStorage.removeItem('just_logged_in');
    setIsOpen(false);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(stats.referralLink);
    toast({
      title: 'Link copiado!',
      description: 'Compartilhe com seus amigos e ganhe créditos',
    });
  };

  if (loading) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
              <Gift className="w-6 h-6 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-xl">Indique e Ganhe!</DialogTitle>
              <DialogDescription className="text-sm">
                Ganhe créditos grátis indicando amigos
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg p-4 border border-primary/20">
            <div className="flex items-center gap-3 mb-3">
              <Users className="w-5 h-5 text-primary" />
              <span className="font-semibold">Como funciona?</span>
            </div>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">1.</span>
                Compartilhe seu link de indicação
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">2.</span>
                Seu amigo se cadastra e faz a primeira compra
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">3.</span>
                Você ganha <strong className="text-primary">10 créditos grátis!</strong>
              </li>
            </ul>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Seu link de indicação:</label>
            <div className="flex gap-2">
              <Input
                value={stats.referralLink}
                readOnly
                className="font-mono text-sm bg-secondary"
              />
              <Button onClick={handleCopyLink} size="icon" variant="outline">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Seu código: <span className="font-mono text-primary">{stats.code}</span>
            </p>
          </div>

          <div className="flex items-center space-x-2 pt-2">
            <Checkbox
              id="dontShowAgain"
              checked={dontShowAgain}
              onCheckedChange={(checked) => setDontShowAgain(checked === true)}
            />
            <label
              htmlFor="dontShowAgain"
              className="text-sm text-muted-foreground cursor-pointer"
            >
              Não mostrar novamente
            </label>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleClose} className="flex-1">
            Fechar
          </Button>
          <Link to="/dashboard/indicacoes" className="flex-1">
            <Button variant="default" className="w-full gap-2" onClick={handleClose}>
              <Users className="w-4 h-4" />
              Ver Indicações
            </Button>
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}
